import type { Mutation } from '@tanstack/react-query';

import { SNACKBAR_STORE } from 'state/snackbar/snackbarStore';
import { displayError } from 'utils/strings/showErrorString';

/**
 * Set `meta: { showGlobalErrorSnackbar: true }` on a mutation to have its failures reported by the
 * app-wide snackbar. Only mutations with no other error surface should opt in — one whose error is
 * already rendered inline, or handled by an `onError`, would tell the user twice.
 */
export const SHOW_GLOBAL_ERROR_SNACKBAR = 'showGlobalErrorSnackbar';

/**
 * `MutationCache.onError` handler: baseline feedback for mutations that would otherwise fail
 * completely silently, leaving the user unable to tell a failure from a no-op.
 *
 * Opt-in rather than opt-out on purpose. An opt-out default cannot be made correct here, because the
 * only handler this callback can see is `mutation.options.onError` — the one passed to `useMutation`.
 * Call sites that handle errors per-call (`mutate(vars, { onError })`) keep those callbacks in the
 * observer's private `#mutateOptions`, which never reaches `mutation.options`, so an opt-out default
 * would double-report them (`ImportsPage.tsx` and `AddUserToSubscriptionButton.tsx` both do this
 * today) — as would every dialog that renders its error inline off `isError` or a `mutateAsync`
 * catch. Making the snackbar the app-wide default is still worth doing, but it needs its own sweep
 * of those call sites rather than riding along with a feature fix.
 */
export function showGlobalMutationErrorSnackbar(
  error: unknown,
  mutation: Pick<Mutation<unknown, unknown, unknown, unknown>, 'meta'>
): void {
  if (!mutation.meta?.[SHOW_GLOBAL_ERROR_SNACKBAR]) {
    return;
  }

  const message = displayError(error);

  SNACKBAR_STORE.getState().showSnackbar(message || 'Something went wrong. Please try again.', 'error', false);
}
